import React from 'react';
import { formatDate, DATE, WEEKDAY } from '@koob/dates';
import {
  hasHotelLocationConflict,
  getDayEffectiveEndPosition,
} from '@koob/utils';
import TripDayCard from './TripDayCard';
import TripDayItemSelectorDisabled from './TripDayItemSelectorDisabled';
import TripDayInsertBetween from './TripDayInsertBetween';
import useTripStore from '../store/useTripStore';
import { useDay, useDays, useHotel } from '../hooks/useTripStoreSelectors';
import { useExperienceField } from '../hooks/useTripDataFields';
import { useIsDayEditable } from '../hooks/useIsDayEditable';
import {
  getDayAccommodationState,
  DayAccommodationState,
} from '../logic/getDayAccommodationState';
import { getDayLocations } from '../logic/getDayLocations';

interface TripDayAccommodationSlotProps {
  dayIndex: number;
  date: any;
  accommodationState: DayAccommodationState;
  readOnly: boolean;
  registerHotelWarning?: (...args: any[]) => void;
  translation: (key: string, options?: Record<string, any>) => string;
  renderHotelSelector: TripDayProps['renderHotelSelector'];
  onResetBookOnMyOwn: () => void;
}

// One case per accommodation state — kept as its own component so `TripDay`
// itself stays under the overlay's max-lines-per-function cap.
function TripDayAccommodationSlot({
  dayIndex,
  date,
  accommodationState,
  readOnly,
  registerHotelWarning,
  translation: t,
  renderHotelSelector,
  onResetBookOnMyOwn,
}: TripDayAccommodationSlotProps) {
  switch (accommodationState.kind) {
    case 'bookedOnMyOwn':
      return (
        <TripDayItemSelectorDisabled>
          {t('compose.hotelWillBeBookedOnMyOwn')}

          {!readOnly && (
            <button
              onClick={onResetBookOnMyOwn}
              className="mt-1.5 border-none text-orange-500"
            >
              {t('compose.resetChoice')}
            </button>
          )}
        </TripDayItemSelectorDisabled>
      );
    case 'hotelSelector':
      return (
        <>
          {renderHotelSelector(dayIndex, {
            date,
            registerHotelWarning,
            readOnly,
          })}
        </>
      );
    case 'nightTransfer':
      return (
        <TripDayItemSelectorDisabled>
          {accommodationState.accommodationName}
        </TripDayItemSelectorDisabled>
      );
    default:
      return (
        <TripDayItemSelectorDisabled>
          {t('compose.noAccomodation')}
        </TripDayItemSelectorDisabled>
      );
  }
}

interface TripDayProps {
  dayIndex: number;
  date: any;
  // True on the first day of a frame: the separator that opens the block is
  // drawn by the timeline, above the frame, not between the header and day 1.
  insertBeforeHandledByFrame?: boolean;
  // KD-3581 — handed down by the frame this day belongs to, and only
  // forwarded: the day itself has no use for them (AC03, AC09).
  globalServicesSlot?: React.ReactNode;
  registerRemoveHandler?: (...args: any[]) => void;
  onExperienceRemoved?: (...args: any[]) => void;
  // KD-3643 — TO-only: the trip warns when a stay can't be booked; the day
  // itself has no use for it, only forwards it to the hotel slot.
  registerHotelWarning?: (...args: any[]) => void;
  registerExperienceWarning?: (...args: any[]) => void;
  // DMC-only: a draft trip may confirm a partner price shift before
  // committing a day insert/remove; forwarded to TripDayInsertBetween as-is.
  confirmPartnerPriceShifts?: (newDays: any[]) => Promise<boolean> | boolean;
  translation: (key: string, options?: Record<string, any>) => string;
  // The experience and hotel slots stay app-local (still 1000+ line files,
  // moving in later stories) — TripDay only decides *whether* and *with what
  // computed props* to render them.
  renderExperience: (
    dayIndex: number,
    props: {
      date: any;
      itineraryLabel: string;
      globalServicesSlot?: React.ReactNode;
      registerRemoveHandler?: (...args: any[]) => void;
      onExperienceRemoved?: (...args: any[]) => void;
      registerExperienceWarning?: (...args: any[]) => void;
      readOnly: boolean;
    },
  ) => React.ReactNode;
  renderHotelSelector: (
    dayIndex: number,
    props: {
      date: any;
      registerHotelWarning?: (...args: any[]) => void;
      readOnly: boolean;
    },
  ) => React.ReactNode;
}

export default function TripDay({
  dayIndex,
  date,
  insertBeforeHandledByFrame,
  globalServicesSlot,
  registerRemoveHandler,
  onExperienceRemoved,
  registerHotelWarning,
  registerExperienceWarning,
  confirmPartnerPriceShifts,
  translation: t,
  renderExperience,
  renderHotelSelector,
}: TripDayProps) {
  const day = useDay(dayIndex);
  const days = useDays();
  const hotel = useHotel(dayIndex);
  const [{ value: experience }] = useExperienceField(dayIndex);
  const experiencesMap = useTripStore((state) => state.experiences);
  const updateDay = useTripStore((state) => state.updateDay);

  // On a partner trip a DMC may only modify the days/products it is
  // responsible for. Days owned by another DMC render read-only. TO has no
  // ownership concept — `useIsDayEditable` defaults to "every day editable".
  const readOnly = !useIsDayEditable(day, experiencesMap);

  const program = experience?.programs?.[(experience?.dayIndex ?? 1) - 1];
  const nextExperienceId = days?.[dayIndex + 1]?.experienceId;
  const effectiveEndPosition = getDayEffectiveEndPosition({
    days,
    dayIndex,
    experiencesMap,
    experience,
    program,
  });

  const hasHotelLocationMismatch = hasHotelLocationConflict({
    hotel,
    program,
    // KD-3635 — the badge follows where the day actually ends, like the day
    // label above, or the two would disagree on the same conflict (KD-3548).
    endPosition: effectiveEndPosition,
  });

  const bookOnMyOwnValue = day?.hotelBookedOnMyOwn;

  const accommodationState = getDayAccommodationState({
    accommodationType: program?.accomodationType,
    accommodationName: program?.accomodationExperience?.name,
    bookOnMyOwnValue,
  });

  const previousExperienceId = days?.[dayIndex - 1]?.experienceId;
  const previousExperience = previousExperienceId
    ? experiencesMap?.[previousExperienceId]
    : null;
  const isDisabled =
    nextExperienceId !== experience?.id &&
    previousExperience?.incompatibilities?.some(
      (item: any) => item?.id === nextExperienceId,
    );

  const { startLocation, endLocation } = getDayLocations({
    program,
    effectiveEndPosition,
  });

  return (
    <div data-testid={`compose-day-${dayIndex}`}>
      {!insertBeforeHandledByFrame && (
        <TripDayInsertBetween
          dayIndex={dayIndex}
          position="before"
          experience={experience}
          isDisabled={isDisabled}
          translation={t}
          confirmPartnerPriceShifts={confirmPartnerPriceShifts}
        />
      )}
      <div className="my-3">
        <TripDayCard
          dayLabel={`${t('compose.day')} ${dayIndex + 1}`}
          weekdayLabel={formatDate(date, WEEKDAY)}
          dateLabel={date ? formatDate(date, DATE) : '--/--/----'}
          program={renderExperience(dayIndex, {
            date,
            // KD-3678 — the itinerary is a qualifier of the program name, so
            // it is rendered under it inside the card rather than as a row
            // of its own above it.
            itineraryLabel: t('compose.frame.itinerary', {
              from: startLocation ?? '--',
              to: endLocation ?? startLocation ?? '--',
            }),
            globalServicesSlot,
            registerRemoveHandler,
            onExperienceRemoved,
            registerExperienceWarning,
            readOnly,
          })}
          accommodation={
            <TripDayAccommodationSlot
              dayIndex={dayIndex}
              date={date}
              accommodationState={accommodationState}
              readOnly={readOnly}
              registerHotelWarning={registerHotelWarning}
              translation={t}
              renderHotelSelector={renderHotelSelector}
              onResetBookOnMyOwn={() =>
                updateDay(dayIndex, { hotelBookedOnMyOwn: undefined })
              }
            />
          }
        />
      </div>

      {hasHotelLocationMismatch && (
        <div className="mt-2 flex justify-center">
          <div className="flex items-center gap-1 rounded-full border border-orange-400 px-3 py-1 text-orange-500">
            <i
              className="fa-light fa-triangle-exclamation"
              style={{ fontSize: 18, color: 'currentColor' }}
            />
            <span className="text-sm font-medium">
              {t('compose.potentialConflict')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
